// Organization Management Page (Separate Page, not in tabs)
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Space, Button, Tag, Modal, notification } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  ThemeButton,
  ThemeFilterBtn,
  ThemeInput,
  ThemeMainWrapper,
  ThemeTable,
  ThemeTableCard,
} from '../Styles/Theme';
import { ForbiddenWrapper } from './OrganizationStyle';
import MobHeader from '../../Components/Header/MobHeader';
import { GreetingText } from '../Dashboard/DashboardStyle';
import {
  getOrganizationsApi,
  getOrganizationsReset,
  deleteOrganizationApi,
  deleteOrganizationReset,
} from '../../store/actions/organizationAction';
import { useOrganizationPermissions } from '../../utils/permissions';
import { STORAGE_KEYS, USER_TYPES } from '../../utils/constants';
import StatusTag from '../../Components/StatusTag';

const Organization = (props) => {
	const {
		getOrganizations,
		getOrganizationsReset,
		getOrganizationsState,
		deleteOrganization,
		deleteOrganizationReset,
		deleteOrganizationState,
	} = props;
	const navigate = useNavigate();

	const [tableData, setTableData] = useState([]);
	const [totalRecords, setTotalRecords] = useState(0);
	const [filters, setFilters] = useState({
		page: 1,
		limit: 12,
		search: '',
	});
	const [deleteModalVisible, setDeleteModalVisible] = useState(false);
	const [selectedOrganization, setSelectedOrganization] = useState(null);

	// Check permissions using hook
	const { can, permissions } = useOrganizationPermissions();
	const canCreate = can('create');
	const canEdit = can('edit');
	const canDelete = can('delete');

	// Fetch organizations
	useEffect(() => {
		if (permissions.authorized) {
			getOrganizations(filters);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, permissions.authorized]);

	// Handle organizations response
	useEffect(() => {
		if (getOrganizationsState.apiState === 'success' && getOrganizationsState.data) {
			// Handle both response structures: { organizations: [...] } or { data: { organizations: [...] } }
			const orgsData = getOrganizationsState.data.data || getOrganizationsState.data;
			if (orgsData.organizations && orgsData.organizations.length > 0) {
				setTotalRecords(orgsData.pagination?.total || getOrganizationsState.data.pagination?.total || 0);
				const updatedOrganizations = orgsData.organizations.map((item) => ({
					key: item._id,
					id: item._id,
					name: item.name,
					contact_person: item.contact_person,
					contact_person_email: item.contact_person_email || item.contact_person_email_address,
					contact_person_mobile: item.contact_person_mobile || item.contact_person_mobile_number,
					city: item.city || '-',
					state: item.state || '-',
					userCount: item.userCount || 0,
					is_active: item.is_active,
					createdAt: item.createdAt,
				}));
				setTableData(updatedOrganizations);
			} else {
				setTableData([]);
				setTotalRecords(0);
			}
			getOrganizationsReset();
		} else if (getOrganizationsState.apiState === 'error') {
			notification.error({
				message: 'Error',
				description: getOrganizationsState.message || 'Failed to fetch organizations',
			});
			getOrganizationsReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getOrganizationsState]);

	// Handle delete response
	useEffect(() => {
		if (deleteOrganizationState.apiState === 'success') {
			notification.success({
				message: 'Success',
				description: deleteOrganizationState.message || 'Organization deleted successfully',
			});
			setDeleteModalVisible(false);
			setSelectedOrganization(null);
			getOrganizations(filters);
		} else if (deleteOrganizationState.apiState === 'error') {
			notification.error({
				message: 'Error',
				description: deleteOrganizationState.message || 'Failed to delete organization',
			});
		}
		deleteOrganizationReset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deleteOrganizationState, filters]);

	const handleSearch = (value) => {
		setFilters({ ...filters, search: value, page: 1 });
	};

	const handleTableChange = (pagination) => {
		setFilters({
			...filters,
			page: pagination.current,
			limit: pagination.pageSize,
		});
	};

	const handleEdit = (record) => {
		navigate(`/organizations/${record.id}`);
	};

	const handleAdd = () => {
		navigate('/organizations/new');
	};

	const handleDelete = (record) => {
		setSelectedOrganization(record);
		setDeleteModalVisible(true);
	};

	const confirmDelete = () => {
		if (selectedOrganization) {
			deleteOrganization({ id: selectedOrganization.id });
		}
	};

	const columns = [
		{
			title: 'Organization Name',
			dataIndex: 'name',
			key: 'name',
			sorter: true,
		},
		{
			title: 'Contact Person',
			dataIndex: 'contact_person',
			key: 'contact_person',
		},
		{
			title: 'Email',
			dataIndex: 'contact_person_email',
			key: 'contact_person_email',
		},
		{
			title: 'Mobile',
			dataIndex: 'contact_person_mobile',
			key: 'contact_person_mobile',
		},
		{
			title: 'Location',
			key: 'location',
			render: (_, record) => {
				const city = record.city && record.city !== '-' ? record.city : null;
				const state = record.state && record.state !== '-' ? record.state : null;
				
				if (city && state) {
					return `${city}, ${state}`;
				}
				if (city) return city;
				if (state) return state;
				return '-';
			},
		},
		{
			title: 'Users',
			dataIndex: 'userCount',
			key: 'userCount',
			render: (count) => <Tag>{count} user(s)</Tag>,
		},
		{
			title: 'Status',
			dataIndex: 'is_active',
			key: 'is_active',
			render: (isActive) => (
				<StatusTag status={isActive ? 'Active' : 'Inactive'} />
			),
		},
		{
			title: 'Actions',
			key: 'actions',
			align: 'center',
			width: 150,
			render: (_, record) => (
				<Space size="middle">
					{canEdit && (
						<Button
							type="link"
							icon={<EditOutlined />}
							onClick={() => handleEdit(record)}
						>
							Edit
						</Button>
					)}
					{canDelete && (
						<Button
							type="link"
							danger
							icon={<DeleteOutlined />}
							onClick={() => handleDelete(record)}
						>
							Delete
						</Button>
					)}
				</Space>
			),
		},
	];

	if (!permissions.authorized) {
		return (
			<ThemeMainWrapper>
				<MobHeader />
				<ForbiddenWrapper>
					<h2>403 - Forbidden</h2>
					<p>You do not have permission to access this page.</p>
				</ForbiddenWrapper>
			</ThemeMainWrapper>
		);
	}

	return (
		<ThemeMainWrapper>
			<MobHeader />
				<Row gutter={[16, 16]} className="mb-6" align="middle">
					<Col xs={24} sm={12} md={12} lg={12}>
						<GreetingText>Organization Management</GreetingText>
					</Col>
					<Col xs={24} sm={12} md={12} lg={12} className="text-end">
						{canCreate && (
							<ThemeButton
								size="large"
								icon={<PlusOutlined />}
								onClick={handleAdd}
							>
								New Organization
							</ThemeButton>
						)}
					</Col>
				</Row>

				<Row gutter={[16, 16]} className="mb-6">
					<Col xs={24} sm={12} md={8} lg={8}>
						<ThemeInput
							placeholder="Search organizations..."
							size="large"
							prefix={<SearchOutlined />}
							allowClear
							onPressEnter={(e) => handleSearch(e.target.value)}
							onChange={(e) => {
								if (!e.target.value) {
									handleSearch('');
								}
							}}
						/>
					</Col>
					{/* <Col xs={24} sm={12} md={8} lg={8}>
						<ThemeFilterBtn size="large" shape="square">
							Filter
						</ThemeFilterBtn>
					</Col> */}
				</Row>

      <ThemeTableCard>
        <ThemeTable
          className="radiusHead"
          columns={columns}
          dataSource={tableData}
          loading={getOrganizationsState.apiState === 'loading'}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: totalRecords,
            showSizeChanger: true,
            showTotal: total => `Total ${total} organizations`,
          }}
          onChange={handleTableChange}
        />
      </ThemeTableCard>

				<Modal
					title="Delete Organization"
					open={deleteModalVisible}
					onOk={confirmDelete}
					onCancel={() => {
						setDeleteModalVisible(false);
						setSelectedOrganization(null);
					}}
					confirmLoading={deleteOrganizationState.apiState === 'loading'}
				>
					<p>
						{`Are you sure you want to delete organization "${selectedOrganization?.name}"?`}
					</p>
					<p>This action cannot be undone.</p>
				</Modal>
		</ThemeMainWrapper>
	);
};

const mapStateToProps = (state) => ({
	getOrganizationsState: state.organization.getOrganizationsState,
	deleteOrganizationState: state.organization.deleteOrganizationState,
});

const mapDispatchToProps = (dispatch) => ({
	getOrganizations: (params) => dispatch(getOrganizationsApi(params)),
	getOrganizationsReset: () => dispatch(getOrganizationsReset()),
	deleteOrganization: (params) => dispatch(deleteOrganizationApi(params.id)),
	deleteOrganizationReset: () => dispatch(deleteOrganizationReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Organization);

